// [FROST BUDDY] morph() lets us swap the new product's <main> in place instead of
// doing a full window.location.href navigation. Pattern mirrors variant-picker.js's
// combined-listing flow (assets/variant-picker.js:439, updateMain).
import { morph, MORPH_OPTIONS } from '@theme/morph';
// ProductSelectEvent (@shopify/events) so sticky-add-to-cart / product-price /
// inventory / media-gallery / fb-bundle-contents / etc. re-sync to the swapped
// product. v4.0.0 consumers listen for StandardEvents.productSelect; we mirror
// variant-picker.js's dispatch shape so they treat the sibling swap identically
// to an internal variant change. Each listener productId-guards.
import { ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that manages a sibling product picker.
 * Products in the specified collection(s) are shown as image swatches.
 * Clicking a swatch AJAX-swaps the new product's <main> in place (no full reload).
 * Hovering a swatch prefetches the sibling's URL so the click swap feels instant.
 */
class SiblingPicker extends HTMLElement {
  /** @type {NodeListOf<Element>} */
  #tabs;

  /** @type {NodeListOf<Element>} */
  #panels;

  /** @type {NodeListOf<HTMLAnchorElement>} */
  #swatches;

  /** @type {AbortController | null} */
  #navAbortController = null;

  /** @type {string | null} */
  #inflightUrl = null;

  /** @type {Set<string>} */
  static #prefetchedUrls = new Set();

  connectedCallback() {
    this.#tabs = this.querySelectorAll('[role="tab"]');
    this.#panels = this.querySelectorAll('[role="tabpanel"]');
    this.#swatches = this.querySelectorAll('[data-sibling-ajax]');

    if (this.#tabs.length > 0) {
      this.#bindTabEvents();
    }

    this.#bindSwatchEvents();
    // [FROST BUDDY] The "See all N designs" expand is handled by a single
    // document-level delegated listener in picker-layout.js (loaded `defer` in
    // <head>), so it works even before this low-priority module upgrades the
    // element. No per-node binding here — see picker-layout.js for the why.
    SiblingPicker.#bindPopstateOnce();
    // [FROST BUDDY] Apply any persisted shopper layout preference BEFORE the
    // horizontal-mode listeners bind, so they see the final attribute value.
    this.#applyPersistedLayoutPreference();
    this.#bindLayoutToggle();
    // [FROST BUDDY] Bind the left-fade scroll listener BEFORE running the
    // scroll-to-selected so the listener captures the resulting scroll change.
    this.#bindHorizontalScrollFade();
    // [FROST BUDDY] On initial load, put the selected tile at the far-left
    // visual position so the shopper sees their current selection first.
    // (Post-click adjustments use a different gated approach — see #performSwap.)
    this.#scrollSelectedToStartOnLoad();
  }

  // [FROST BUDDY] Read the shared shopper preference (set by either picker's
  // segmented toggle) and apply it BEFORE any layout-dependent initialization
  // runs. Also subscribe to future changes so flipping from the variant picker
  // keeps this one in sync.
  //
  // Intentionally does NOT call #applyLayoutSideEffects on init: the grid
  // auto-expand is a side effect of actively tapping the toggle, not of
  // restoring a persisted choice on page load. Horizontal-mode init
  // (scroll-to-selected) runs naturally later in connectedCallback.
  #applyPersistedLayoutPreference() {
    this.#syncLayoutToPersistedPreference();
    window.PickerLayout?.onChange((event) => this.#applyLayout(event.detail.value));
  }

  // [FROST BUDDY] Apply localStorage preference to dataset.mobileLayout (and
  // sync the toggle's aria-pressed). Extracted so it can be re-called after an
  // AJAX morph — morph overwrites data-mobile-layout with the new product's
  // merchant default, which would otherwise revert the shopper's runtime choice.
  #syncLayoutToPersistedPreference() {
    const pref = typeof window !== 'undefined' && window.PickerLayout?.get();
    if (pref && pref !== this.dataset.mobileLayout) {
      this.dataset.mobileLayout = pref;
    }
    this.#syncToggleAriaPressed();
  }

  // [FROST BUDDY] Bind each segment button. Clicking a button writes its
  // data-fb-layout-value to PickerLayout (which fires the change event picked
  // up by both pickers). Clicking the already-active button is a no-op.
  #bindLayoutToggle() {
    const buttons = this.querySelectorAll('.fb-layout-toggle[data-fb-layout-value]');
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.getAttribute('data-fb-layout-value');
        if (target === this.dataset.mobileLayout) return;
        window.PickerLayout?.set(target);
      });
    });
  }

  // [FROST BUDDY] Apply a new layout state — called from the PickerLayout
  // change event (so cross-picker syncing works).
  #applyLayout(value) {
    if (value !== 'vertical' && value !== 'horizontal') return;
    if (this.dataset.mobileLayout === value) {
      this.#syncToggleAriaPressed();
      return;
    }
    this.dataset.mobileLayout = value;
    this.#syncToggleAriaPressed();
    this.#applyLayoutSideEffects(value);
  }

  // [FROST BUDDY] Layout-mode side effects, fired ONLY from the runtime-toggle
  // path (not from initial mount).
  //  - horizontal → re-anchor the scroll position so the selected tile shows.
  //  - vertical (when merchant intended horizontal) → expand the grid so the
  //    shopper sees ALL designs immediately. We don't auto-expand when the
  //    merchant default is already vertical, keeping the existing collapsed +
  //    "See all N designs" expand-button behavior intact for those PDPs.
  #applyLayoutSideEffects(value) {
    if (value === 'horizontal') {
      this.#scrollSelectedToStartOnLoad();
      return;
    }
    if (this.dataset.merchantMobileLayout === 'horizontal') {
      const panel = this.querySelector('.sibling-picker__panel');
      panel?.setAttribute('data-expanded', 'true');
      this.querySelector('[data-grid-expand]')?.setAttribute('aria-expanded', 'true');
    }
  }

  // [FROST BUDDY] Each segment button's aria-pressed reflects whether its
  // data-fb-layout-value matches the current layout.
  #syncToggleAriaPressed() {
    const buttons = this.querySelectorAll('.fb-layout-toggle[data-fb-layout-value]');
    buttons.forEach((button) => {
      const isActive = button.getAttribute('data-fb-layout-value') === this.dataset.mobileLayout;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  // [FROST BUDDY] Toggle a data-scrolled-left attribute on the horizontal grid
  // whenever scrollLeft is past 0, so CSS can show a left-edge fade indicating
  // hidden tiles to the left.
  #bindHorizontalScrollFade() {
    if (this.dataset.mobileLayout !== 'horizontal') return;
    const grid = this.querySelector('.sibling-picker__grid');
    if (!grid) return;

    let rafId = null;
    const update = () => {
      rafId = null;
      if (grid.scrollLeft > 0) {
        grid.setAttribute('data-scrolled-left', '');
      } else {
        grid.removeAttribute('data-scrolled-left');
      }
    };
    grid.addEventListener('scroll', () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(update);
    }, { passive: true });
    // Initial sync in case scrollLeft is already non-zero (e.g. on bfcache restore).
    update();
  }

  // [FROST BUDDY] Initial-load helper: in horizontal mobile mode, position the
  // selected sibling tile so the shopper sees it prominently while preserving
  // the "3 solid in the middle + partial peek on each side" layout. Special
  // cases at the list extremes:
  //  - First tile selected  → flush left   (no previous tile to peek)
  //  - Last tile selected   → flush right  (no next tile to peek)
  //  - Mid-list selected    → inset by scroll-padding-inline so previous tile peeks
  #scrollSelectedToStartOnLoad() {
    if (this.dataset.mobileLayout !== 'horizontal') return;
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 749px)').matches) return;

    const grid = this.querySelector('.sibling-picker__grid');
    const selectedSwatch = grid?.querySelector('.sibling-picker__swatch--selected');
    const selectedItem = selectedSwatch?.closest('.sibling-picker__item');
    if (!grid || !selectedItem) return;

    requestAnimationFrame(() => {
      const gridRect = grid.getBoundingClientRect();
      const selRect = selectedItem.getBoundingClientRect();
      const cs = getComputedStyle(grid);
      const paddingLeft = parseFloat(cs.paddingLeft || '0');
      const paddingRight = parseFloat(cs.paddingRight || '0');
      const scrollPaddingStart = parseFloat(cs.scrollPaddingInlineStart || '0');

      const prev = selectedItem.previousElementSibling;
      const hasPrev = prev && prev.classList.contains('sibling-picker__item');
      const next = selectedItem.nextElementSibling;
      const hasNext = next && next.classList.contains('sibling-picker__item');

      let delta;
      if (!hasPrev) {
        // First tile — flush left.
        delta = selRect.left - (gridRect.left + paddingLeft);
      } else if (!hasNext) {
        // Last tile — flush right.
        delta = selRect.right - (gridRect.right - paddingRight);
      } else {
        // Mid-list — partial peek on left, selected at first solid spot.
        delta = selRect.left - (gridRect.left + paddingLeft + scrollPaddingStart);
      }
      // Browsers clamp scrollLeft to [0, maxScrollLeft] so we don't need to.
      grid.scrollLeft += delta;
    });
  }

  // [FROST BUDDY] Right-edge fade is `mask-image: linear-gradient(... #000 88%,
  // transparent 100%)` in section 17 of custom.css. We want the selected tile to
  // sit fully solid with the NEXT tile being the one that fades — so we align the
  // tile after the selected to the 88% mark. If the selected tile is the very
  // last item in the list, we fall back to aligning the selected tile itself.
  #adjustHorizontalSelectedIntoView() {
    if (this.dataset.mobileLayout !== 'horizontal') return;
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 749px)').matches) return;

    const grid = this.querySelector('.sibling-picker__grid');
    const selectedSwatch = grid?.querySelector('.sibling-picker__swatch--selected');
    const selectedItem = selectedSwatch?.closest('.sibling-picker__item');
    if (!grid || !selectedItem) return;

    // Defer one frame so the layout (and any prior scroll position) is settled.
    requestAnimationFrame(() => {
      const gridRect = grid.getBoundingClientRect();
      const fadeStartRatio = 0.89;
      const fadeStartX = gridRect.left + gridRect.width * fadeStartRatio;

      // Gate: only adjust when the selected tile is actually being faded
      // (its right edge is past the fade-start mark — i.e., it's in the fade
      // zone or scrolled completely off the right). Middle-of-grid selections
      // stay where they are.
      const selRect = selectedItem.getBoundingClientRect();
      if (selRect.right <= fadeStartX) return;

      const nextItem = selectedItem.nextElementSibling;
      const hasNext = nextItem && nextItem.classList.contains('sibling-picker__item');

      if (hasNext) {
        // Align the next tile's LEFT edge to the fade-start mark so it becomes
        // the partially-faded one. The selected tile shifts left into the last
        // fully-solid slot; any prior tiles scroll off the left.
        const nextRect = nextItem.getBoundingClientRect();
        grid.scrollLeft += nextRect.left - fadeStartX;
      } else {
        // Selected is the last tile — no next sibling to fade. Align the
        // selected tile's RIGHT edge to the fade-start mark so it sits just
        // before the fade zone (the furthest-right fully-solid position).
        grid.scrollLeft += selRect.right - fadeStartX;
      }
    });
  }

  disconnectedCallback() {
    // Cancel any in-flight swap when the picker leaves the DOM.
    this.#navAbortController?.abort();
  }

  #bindTabEvents() {
    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', this.#handleTabClick.bind(this));
      tab.addEventListener('keydown', this.#handleTabKeydown.bind(this));
    });
  }

  #bindSwatchEvents() {
    this.#swatches.forEach((swatch) => {
      swatch.addEventListener('click', this.#handleSwatchClick.bind(this));
      // pointerenter: mouse hover (desktop) | pointerdown: first touch contact
      // (mobile) — gives the prefetch a 100–300 ms head start before `click` fires.
      swatch.addEventListener('pointerenter', this.#handleSwatchHover.bind(this));
      swatch.addEventListener('pointerdown', this.#handleSwatchHover.bind(this));
    });
  }

  /**
   * Handles hover OR initial touch on a sibling swatch — prefetches the sibling's
   * URL so the subsequent AJAX swap can serve from cache. No visual preview side
   * effects (the gallery + title stay on the current product until click). Runs
   * for any pointer type (mouse, touch, pen).
   * @param {PointerEvent} event - The pointer event.
   */
  #handleSwatchHover(event) {
    if (!(event instanceof PointerEvent)) return;

    const swatch = /** @type {HTMLAnchorElement} */ (event.currentTarget);

    // Don't prefetch when targeting the currently-selected product.
    if (swatch.getAttribute('aria-current') === 'true') return;

    const productUrl = swatch.dataset.productUrl;
    if (!productUrl || SiblingPicker.#prefetchedUrls.has(productUrl)) return;

    SiblingPicker.#prefetchedUrls.add(productUrl);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = productUrl;
    document.head.appendChild(link);
  }

  /**
   * Handles the swatch click event — AJAX-swaps the sibling product in place.
   * @param {Event} event - The click event
   */
  #handleSwatchClick(event) {
    event.preventDefault();

    const swatch = /** @type {HTMLAnchorElement} */ (event.currentTarget);
    const productUrl = swatch.dataset.productUrl;
    const currentProductUrl = this.dataset.productUrl;

    // Don't reload if clicking the currently selected product
    if (!productUrl || productUrl === currentProductUrl) return;

    // Update URL via replaceState so back button skips siblings entirely.
    this.#performSwap(productUrl, /* updateUrl */ true);
  }

  /**
   * Fetches the destination PDP, morphs <main>, restores picker UI state.
   * Falls back to a full navigation on any failure so a shopper is never stranded.
   * @param {string} productUrl - URL of the sibling product to load.
   * @param {boolean} updateUrl - Whether to history.replaceState the new URL (false for popstate-driven swaps where history already moved).
   */
  #performSwap(productUrl, updateUrl) {
    // [FROST BUDDY] Coalesce: if a swap to this exact URL is already in flight,
    // ignore the duplicate (covers double-tap, hover-then-click on the same swatch,
    // or popstate firing while a swap is mid-flight). Different URL still aborts.
    if (this.#inflightUrl === productUrl) return;
    this.#inflightUrl = productUrl;

    // Cancel any in-flight swap to a DIFFERENT URL so a rapid second click doesn't race.
    this.#navAbortController?.abort();
    this.#navAbortController = new AbortController();

    // Capture picker UI state BEFORE the morph overwrites it. The new page's
    // sibling-picker block ships with data-expanded="false" and
    // data-active-bucket="all" — we want the shopper's choices preserved.
    const panel = this.querySelector('.sibling-picker__panel');
    const wasExpanded = panel?.getAttribute('data-expanded') === 'true';
    const activeBucket = panel?.getAttribute('data-active-bucket') || 'all';
    const activeTab = /** @type {HTMLElement | null} */ (
      this.querySelector('[role="tab"][aria-selected="true"]')
    );
    const activeTabBucket = activeTab?.dataset.bucket;

    // [FROST BUDDY] Capture sticky-bar visibility too — our morph will wipe
    // data-stuck to the new HTML's default (false). If we don't restore it
    // before sticky-add-to-cart's ProductSelectEvent handler runs, the handler
    // captures the wiped 'false' and "restores" it as false (bar stays hidden
    // until the user scrolls and the IntersectionObserver fires).
    const wasStuck = document.querySelector('sticky-add-to-cart [ref="stickyBar"]')?.getAttribute('data-stuck') === 'true';

    // CSS hook for optional loading affordance.
    this.setAttribute('data-fb-swapping', 'true');

    /** @type {number | null} Picker's viewport top just before the morph. */
    let pickerTopBefore = null;

    fetch(productUrl, { signal: this.#navAbortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then((text) => {
        const newDoc = new DOMParser().parseFromString(text, 'text/html');
        const main = document.querySelector('main');
        const newMain = newDoc.querySelector('main');
        if (!main || !newMain) throw new Error('No <main> in response');

        // [FROST BUDDY] Preload the new product's hero image BEFORE morph commits
        // the new <img src>. Browser fetches it concurrently with morph instead of
        // serially after, shaving 50–200 ms off above-the-fold LCP on mobile.
        SiblingPicker.#preloadHeroImage(newDoc);

        // [FROST BUDDY] Suspend browser scroll anchoring across the morph and pin the
        // picker to its pre-swap viewport offset instead. Cross-template swaps (e.g.
        // team Product row: product.universal-xl → product) reshuffle the layout, and
        // anchoring latched onto a node that moved far down — the page jumped ~1200px
        // past the picker on mobile. Re-pinned after the morph below.
        pickerTopBefore = this.getBoundingClientRect().top;
        document.documentElement.style.overflowAnchor = 'none';

        morph(main, newMain, MORPH_OPTIONS);

        // [FROST BUDDY] <template> elements hold their HTML inside `.content`
        // (DocumentFragment), which is invisible to morph — so morph leaves the
        // old template's data unchanged. Manually copy fresh innerHTML from
        // newDoc into any matching same-ID templates so the next re-init clones
        // the new product's data (correct chosen_variant per the new design).
        newDoc.querySelectorAll('template[id]').forEach((newTpl) => {
          const oldTpl = document.getElementById(newTpl.id);
          if (oldTpl instanceof HTMLTemplateElement && newTpl instanceof HTMLTemplateElement) {
            oldTpl.innerHTML = newTpl.innerHTML;
          }
        });

        // Re-init any FB sections that inject themselves into anchors outside
        // their own template (e.g. fb-product-accessories renders into a
        // <template> then clones into #fb-accessories on first load). Morph
        // rips out the previous clone; each section exposes
        // window.fbLoadAccessories_<id> explicitly so AJAX navigation can re-inject.
        for (const key in window) {
          if (key.indexOf('fbLoadAccessories_') === 0 && typeof window[key] === 'function') {
            try { window[key](); } catch (err) { console.error('[sibling-picker] reinit ' + key + ' failed', err); }
          }
        }

        // Restore picker UI state on the post-morph DOM. Re-query the picker since
        // the morph may have detached the previous reference.
        const newPicker = document.querySelector('sibling-picker');
        const newPanel = newPicker?.querySelector('.sibling-picker__panel');
        if (newPanel) {
          // [FROST BUDDY] Only carry the bucket over if the new page offers that pill
          // (team-mode pages can drop ONE Lid/Seasonal, or hide the pill row entirely);
          // otherwise the grid would stay filtered with no control to reset it.
          const bucketAvailable =
            activeBucket === 'all' || !!newPicker?.querySelector(`[role="tab"][data-bucket="${activeBucket}"]`);
          const restoredBucket = bucketAvailable ? activeBucket : 'all';
          newPanel.setAttribute('data-active-bucket', restoredBucket);
          if (wasExpanded) {
            newPanel.setAttribute('data-expanded', 'true');
            newPicker?.querySelector('[data-grid-expand]')?.setAttribute('aria-expanded', 'true');
          }
          if (activeTabBucket) {
            const restoredTabBucket = bucketAvailable ? activeTabBucket : 'all';
            newPicker?.querySelectorAll('[role="tab"]').forEach((t) => {
              const isActive = /** @type {HTMLElement} */ (t).dataset.bucket === restoredTabBucket;
              t.setAttribute('aria-selected', String(isActive));
              t.setAttribute('tabindex', isActive ? '0' : '-1');
            });
          }
        }

        // [FROST BUDDY] Re-apply the shopper's persisted layout preference.
        // morph overwrote data-mobile-layout with the new product's merchant
        // default (typically "horizontal"); without this, the shopper's
        // runtime toggle to vertical would revert on every sibling swap.
        if (newPicker instanceof SiblingPicker) {
          newPicker.#syncLayoutToPersistedPreference();
        }

        if (updateUrl) {
          // replaceState (not pushState) so back-button skips intermediate
          // siblings the shopper browsed and returns to whatever they were on
          // before they landed on the first sibling PDP.
          history.replaceState({ fbSiblingSwap: true }, '', productUrl);
        }

        const newTitle = newDoc.querySelector('title')?.textContent;
        if (newTitle) document.title = newTitle;

        // [FROST BUDDY] Re-stick BEFORE dispatching ProductSelectEvent. The handler
        // captures the current data-stuck value to preserve it across its own inner
        // morph — if we leave it 'false' here, the bar stays hidden until scroll.
        if (wasStuck) {
          document.querySelector('sticky-add-to-cart [ref="stickyBar"]')?.setAttribute('data-stuck', 'true');
        }

        // [FROST BUDDY] Fire ProductSelectEvent so sticky-add-to-cart restores its
        // data-stuck state, re-caches the ATC button reference, and product-price /
        // inventory / media-gallery / etc. re-sync to the new product. Mirrors what
        // variant-picker.js does post-fetch (assets/variant-picker.js:333).
        const newSticky = document.querySelector('sticky-add-to-cart');
        const newProductId = newSticky?.dataset.productId || '';

        // [FROST BUDDY] Re-parse the HTML for the event payload AND variantData.
        // Idiomorph's updateChildren MOVES nodes (appendChild/insertBefore) from
        // newDoc into the live DOM during the morph above — so by this point
        // newDoc's subtrees (e.g. variant-picker's <script type="application/json">,
        // zoom-dialog's `<li ref="media[]">`) have been gutted. If we pass the
        // consumed newDoc to media-gallery's handler, it queries an empty
        // <media-gallery> shell and replaceWith()-s the correctly-morphed live one
        // with that empty shell, breaking zoom. Same problem reading variantData.
        const freshDoc = new DOMParser().parseFromString(text, 'text/html');

        // [FROST BUDDY] Extract the new product's selected variant. Multi-variant
        // products dump the full Liquid variant JSON in `<variant-picker script>`;
        // single-variant products (has_only_default_variant) skip the variant-picker
        // entirely (snippets/variant-main-picker.liquid:12), so we always fall back
        // to the `<input ref="variantId">` value that buy-buttons.liquid:67 renders
        // from `product.selected_or_first_available_variant.id`. Without this
        // fallback, product-form's #onProductSelect sets variantId.value = '' on
        // single-variant siblings and breaks add-to-cart.
        let variantData = null;
        try {
          const variantJson = freshDoc.querySelector('variant-picker script[type="application/json"]')?.textContent;
          if (variantJson) variantData = JSON.parse(variantJson);
        } catch (_) {}
        if (!variantData) {
          const fallbackVariantId = freshDoc.querySelector('[ref="variantId"]')?.value;
          if (fallbackVariantId) {
            // [FROST BUDDY] Read sold-out state from the fetched ATC button. Without
            // this, product-form.js's #onProductSelect sees `resource.available: true`
            // for sold-out single-variant siblings and leaves the button enabled.
            // Default to available=true if we can't find the button (defensive — don't
            // accidentally disable a healthy variant on a parsing edge case).
            const fallbackAtcBtn = freshDoc.querySelector('product-form-component [ref="addToCartButton"]');
            const fallbackAvailable = !(fallbackAtcBtn?.hasAttribute('disabled') ?? false);
            variantData = { id: fallbackVariantId, available: fallbackAvailable };
          }
        }
        if (newProductId) {

          // [FROST BUDDY] Build a ProductSelectEvent with a pre-resolved promise.
          // variant-picker.js uses createPromise + async resolve after fetch; we
          // already have the data (morph is done), so resolve immediately. Shape
          // matches variant-picker's resolve (variant-picker.js:333) so consumers
          // can read event.promise.then(({ variant, detail }) => …).
          const deferredEventPromise = ProductSelectEvent.createPromise();
          const selectedOptions = (variantData?.options ?? []).map((value, idx) => ({
            name: variantData?.option_names?.[idx] ?? String(idx),
            value,
          }));

          (newSticky || document.body).dispatchEvent(
            new ProductSelectEvent({
              product: {
                id: newProductId,
                title: variantData?.product_title ?? '',
                handle: variantData?.product_handle ?? '',
              },
              selectedOptions,
              detail: { sourceId: 'sibling-picker' },
              promise: deferredEventPromise.promise,
            })
          );

          deferredEventPromise.resolve({
            variant: variantData ?? null,
            detail: {
              html: freshDoc,
              productId: newProductId,
              sourceId: 'sibling-picker',
              resource: variantData,
            },
          });
        }

        // [FROST BUDDY] After the morph the previously-clicked tile is now
        // .sibling-picker__swatch--selected (in the same scroll position the
        // user clicked from). If it landed in the right-edge fade zone, shift
        // it into the solid area — same gating as the initial-load adjustment.
        if (newPicker instanceof SiblingPicker) {
          newPicker.#adjustHorizontalSelectedIntoView();
        }

        // [FROST BUDDY] Keep the picker under the shopper's finger (see overflowAnchor above).
        if (newPicker && pickerTopBefore !== null) {
          window.scrollBy(0, newPicker.getBoundingClientRect().top - pickerTopBefore);
        }

        this.removeAttribute('data-fb-swapping');
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        // Hard-nav fallback — shopper never gets stranded.
        window.location.href = productUrl;
      })
      .finally(() => {
        // Re-enable scroll anchoring once the post-morph layout has settled.
        if (pickerTopBefore !== null) {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => document.documentElement.style.removeProperty('overflow-anchor'))
          );
        }
        // Release the duplicate-guard regardless of outcome.
        if (this.#inflightUrl === productUrl) this.#inflightUrl = null;
      });
  }

  // [FROST BUDDY] Inject <link rel="preload" as="image" fetchpriority="high">
  // for the new product's hero image so the browser starts fetching it during
  // morph instead of waiting until after the new <img> is in the DOM.
  // Previous swap's preload is removed first to keep <head> tidy.
  static #preloadHeroImage(newDoc) {
    document.querySelectorAll('link[data-fb-hero-preload]').forEach((el) => el.remove());
    const hero = /** @type {HTMLImageElement | null} */ (
      newDoc.querySelector('media-gallery img, .product-media__image, media-gallery source')
    );
    const href = hero?.getAttribute('src') || hero?.getAttribute('srcset')?.split(',')[0]?.trim().split(/\s+/)[0];
    if (!href) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    link.setAttribute('fetchpriority', 'high');
    link.dataset.fbHeroPreload = 'true';
    const srcset = hero?.getAttribute('srcset');
    const sizes = hero?.getAttribute('sizes');
    if (srcset) link.setAttribute('imagesrcset', srcset);
    if (sizes) link.setAttribute('imagesizes', sizes);
    document.head.appendChild(link);
  }

  // [FROST BUDDY] One-time binding so browser back/forward also AJAX-swaps instead
  // of reloading. Idempotent: only the first picker to mount wires the listener.
  static #popstateBound = false;
  static #bindPopstateOnce() {
    if (SiblingPicker.#popstateBound) return;
    SiblingPicker.#popstateBound = true;
    window.addEventListener('popstate', () => {
      const picker = /** @type {SiblingPicker | null} */ (document.querySelector('sibling-picker'));
      if (!picker) {
        window.location.reload();
        return;
      }
      // Skip the URL update — popstate already moved the URL for us.
      picker.#performSwap(window.location.href, /* updateUrl */ false);
    });
  }

  /**
   * Handles tab click events
   * @param {Event} event - The click event
   */
  #handleTabClick(event) {
    const clickedTab = /** @type {HTMLElement} */ (event.currentTarget);
    this.#activateTab(clickedTab);
  }

  /**
   * Handles tab keyboard navigation
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleTabKeydown(event) {
    const currentIndex = Array.from(this.#tabs).indexOf(/** @type {Element} */ (event.currentTarget));
    let newIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = this.#tabs.length - 1;
        /** @type {HTMLElement} */ (this.#tabs[newIndex]).focus();
        this.#activateTab(/** @type {HTMLElement} */ (this.#tabs[newIndex]));
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex + 1;
        if (newIndex >= this.#tabs.length) newIndex = 0;
        /** @type {HTMLElement} */ (this.#tabs[newIndex]).focus();
        this.#activateTab(/** @type {HTMLElement} */ (this.#tabs[newIndex]));
        break;
      case 'Home':
        event.preventDefault();
        /** @type {HTMLElement} */ (this.#tabs[0]).focus();
        this.#activateTab(/** @type {HTMLElement} */ (this.#tabs[0]));
        break;
      case 'End':
        event.preventDefault();
        /** @type {HTMLElement} */ (this.#tabs[this.#tabs.length - 1]).focus();
        this.#activateTab(/** @type {HTMLElement} */ (this.#tabs[this.#tabs.length - 1]));
        break;
    }
  }

  /**
   * Activates a tab and shows its associated panel
   * @param {HTMLElement} selectedTab - The tab to activate
   */
  #activateTab(selectedTab) {
    const panelId = selectedTab.getAttribute('aria-controls');
    const selectedPanel = panelId ? this.querySelector(`#${panelId}`) : null;

    // Deactivate all tabs
    this.#tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
      tab.classList.remove('sibling-picker__tab--active');
    });

    // Hide all panels and disable focusable elements
    this.#panels.forEach((panel) => {
      /** @type {HTMLElement} */ (panel).hidden = true;
      panel.classList.remove('sibling-picker__panel--active');
      panel.querySelectorAll('a, button').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
    });

    // Activate selected tab
    selectedTab.setAttribute('aria-selected', 'true');
    selectedTab.setAttribute('tabindex', '0');
    selectedTab.classList.add('sibling-picker__tab--active');

    // Show selected panel and enable focusable elements
    if (selectedPanel) {
      /** @type {HTMLElement} */ (selectedPanel).hidden = false;
      selectedPanel.classList.add('sibling-picker__panel--active');
      selectedPanel.querySelectorAll('a, button').forEach((el) => {
        el.removeAttribute('tabindex');
      });

      // [FROST BUDDY] All/Solids/Patterns bucket filter: when the clicked tab carries
      // a data-bucket attribute, mirror it onto the panel so the CSS filter rule in
      // assets/custom.css (section 16) can hide non-matching items.
      const bucket = selectedTab.getAttribute('data-bucket');
      if (bucket) {
        selectedPanel.setAttribute('data-active-bucket', bucket);
        // Filtering to Solids/Patterns implies the shopper wants to browse — commit
        // expanded state so returning to All preserves the full grid (no need to
        // click "See all N designs" again).
        if (bucket !== 'all') {
          selectedPanel.setAttribute('data-expanded', 'true');
          this.querySelector('[data-grid-expand]')?.setAttribute('aria-expanded', 'true');
        }
      }
    }
  }
}

if (!customElements.get('sibling-picker')) {
  customElements.define('sibling-picker', SiblingPicker);
}

export default SiblingPicker;
